import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Building2, Inbox, Plus, X } from 'lucide-react';
import { EnterpriseAdminService } from '../../services/enterpriseAdminService';
import { marketingAdmin } from '../../services/marketingAdminService';

const RATE_PICK_MODES = ['manual', 'lowest_rate', 'nearest', 'bargain'];
const SERVICE_TYPES = ['Towing', 'Jump Start', 'Flat Tyre', 'Minor Repair', 'Key Lockout', 'Fuel Delivery'];

export const EMPTY_FORM = {
  name: '',
  legal_name: '',
  gstin: '',
  contact_person: '',
  contact_phone: '',
  contact_email: '',
  status: 'active',
  per_complaint_fee: 99,
  mechanic_payment_mode: 'collect_from_customer',
  min_wallet_balance: 0,
  consume_partner_ticket: true,
  allow_bargain: true,
  bargain_fee_mode: 'none',
  bargain_fee: 0,
  default_wait_seconds: 300,
  wait_seconds_min: 60,
  wait_seconds_max: 1800,
  allowed_rate_pick_modes: [...RATE_PICK_MODES],
  allowed_service_types: [],
  api_enabled: true,
  rate_limit_per_min: 60,
  webhook_url: '',
  subscription_monthly_inr: 0,
  subscription_autopay: true,
};

export function EnterpriseForm({ initial, onSaved, onCancel, sections = 'all' }) {
  const showCompany = sections === 'all' || sections === 'company';
  const showRates = sections === 'all' || sections === 'rates';
  const showDispatch = sections === 'all' || sections === 'company';
  const showApi = sections === 'all' || sections === 'company';
  const [form, setForm] = useState({ ...EMPTY_FORM, ...(initial || {}) });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const toggleArr = (key, value) => {
    setForm((f) => {
      const arr = Array.isArray(f[key]) ? [...f[key]] : [];
      const idx = arr.indexOf(value);
      if (idx >= 0) arr.splice(idx, 1);
      else arr.push(value);
      return { ...f, [key]: arr };
    });
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setError('Company name is required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        per_complaint_fee: Number(form.per_complaint_fee || 0),
        min_wallet_balance: Number(form.min_wallet_balance || 0),
        bargain_fee: Number(form.bargain_fee || 0),
        subscription_monthly_inr: Number(form.subscription_monthly_inr || 0),
        subscription_autopay: !!form.subscription_autopay,
        default_wait_seconds: Number(form.default_wait_seconds || 300),
        wait_seconds_min: Number(form.wait_seconds_min || 60),
        wait_seconds_max: Number(form.wait_seconds_max || 1800),
        rate_limit_per_min: Number(form.rate_limit_per_min || 60),
        allowed_service_types: Array.isArray(form.allowed_service_types)
          ? form.allowed_service_types
          : [],
      };
      if (!Array.isArray(payload.allowed_rate_pick_modes)) delete payload.allowed_rate_pick_modes;
      if (!Array.isArray(payload.allowed_cities)) delete payload.allowed_cities;
      if (!Array.isArray(payload.allowed_service_types)) delete payload.allowed_service_types;
      const res = await EnterpriseAdminService.upsert(payload);
      if (!res?.success) {
        setError(res?.error || 'Failed to save');
        return;
      }
      const entId = res.id || form.id;
      if (entId) {
        await EnterpriseAdminService.setSubscription(entId, {
          monthlyInr: Number(form.subscription_monthly_inr || 0),
          autopay: !!form.subscription_autopay,
        });
      }
      onSaved?.(res.id);
    } catch (err) {
      const msg = err.message || 'Failed to save';
      setError(
        /admin_enterprise_set_subscription|does not exist|schema cache/i.test(msg)
          ? 'Subscription columns are missing. Run supabase/enterprise-portal-subscription.sql in the SQL Editor, then save again.'
          : msg,
      );
    } finally {
      setSaving(false);
    }
  };

  const input = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent';
  const label = 'block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1';

  return (
    <form onSubmit={save} className="space-y-5">
      {showCompany && (
      <div>
        <h3 className="font-bold text-gray-900 mb-3">Company</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className={label}>Company name *</label>
            <input className={input} value={form.name} onChange={(e) => set('name', e.target.value)} />
          </div>
          <div>
            <label className={label}>Legal name</label>
            <input className={input} value={form.legal_name || ''} onChange={(e) => set('legal_name', e.target.value)} />
          </div>
          <div>
            <label className={label}>GSTIN</label>
            <input className={input} value={form.gstin || ''} onChange={(e) => set('gstin', e.target.value)} />
          </div>
          <div>
            <label className={label}>Status</label>
            <select className={input} value={form.status} onChange={(e) => set('status', e.target.value)}>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
            </select>
          </div>
          <div>
            <label className={label}>Contact person</label>
            <input className={input} value={form.contact_person || ''} onChange={(e) => set('contact_person', e.target.value)} />
          </div>
          <div>
            <label className={label}>Contact phone</label>
            <input className={input} value={form.contact_phone || ''} onChange={(e) => set('contact_phone', e.target.value)} />
          </div>
          <div className="md:col-span-2">
            <label className={label}>Contact email</label>
            <input className={input} type="email" value={form.contact_email || ''} onChange={(e) => set('contact_email', e.target.value)} />
          </div>
        </div>
        <div className="mt-4 rounded-lg border border-indigo-100 bg-indigo-50/60 p-4">
          <h4 className="text-sm font-bold text-gray-900">Portal subscription (monthly)</h4>
          <p className="mt-1 text-xs text-gray-500">
            Whole company account — not per city. Separate from wallet/points and rate cards.
            Click Save Enterprise. The company then sees this under <strong>Subscription</strong> in
            the portal sidebar (localhost:3010). ₹0 = free portal access.
          </p>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className={label}>Monthly fee (₹)</label>
              <input
                className={input}
                type="number"
                min="0"
                value={form.subscription_monthly_inr ?? 0}
                onChange={(e) => set('subscription_monthly_inr', e.target.value)}
              />
            </div>
            <label className="flex items-end gap-2 pb-2 text-sm font-medium text-gray-700">
              <input
                type="checkbox"
                checked={!!form.subscription_autopay}
                onChange={(e) => set('subscription_autopay', e.target.checked)}
              />
              Autopay (Razorpay mandate)
            </label>
          </div>
        </div>
      </div>
      )}

      {showRates && (
      <div>
        <h3 className="font-bold text-gray-900 mb-3">Rates</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className={label}>Fee per completed complaint (₹)</label>
            <input className={input} type="number" min="0" value={form.per_complaint_fee} onChange={(e) => set('per_complaint_fee', e.target.value)} />
          </div>
          <div>
            <label className={label}>Mechanic payment mode</label>
            <select className={input} value={form.mechanic_payment_mode} onChange={(e) => set('mechanic_payment_mode', e.target.value)}>
              <option value="collect_from_customer">Mechanic collects from customer</option>
              <option value="wallet_deduct">Deduct from enterprise wallet</option>
              <option value="roadserve_settle">RoadServe settles (postpaid)</option>
            </select>
          </div>
          <div>
            <label className={label}>Min wallet balance (₹)</label>
            <input className={input} type="number" min="0" value={form.min_wallet_balance} onChange={(e) => set('min_wallet_balance', e.target.value)} />
          </div>
          <div>
            <label className={label}>Bargain / chat fee mode</label>
            <select className={input} value={form.bargain_fee_mode} onChange={(e) => set('bargain_fee_mode', e.target.value)}>
              <option value="none">Free</option>
              <option value="per_chat">Per chat thread</option>
              <option value="per_complaint">Per complaint</option>
            </select>
          </div>
          <div>
            <label className={label}>Bargain fee (₹)</label>
            <input className={input} type="number" min="0" value={form.bargain_fee} onChange={(e) => set('bargain_fee', e.target.value)} />
          </div>
          <div className="flex items-end gap-4 pb-1">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={!!form.allow_bargain} onChange={(e) => set('allow_bargain', e.target.checked)} />
              Allow bargain
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={!!form.consume_partner_ticket} onChange={(e) => set('consume_partner_ticket', e.target.checked)} />
              Consume partner ticket
            </label>
          </div>
        </div>
        <div className="mt-3">
          <label className={label}>Allowed rate pick modes</label>
          <div className="flex flex-wrap gap-3">
            {RATE_PICK_MODES.map((m) => (
              <label key={m} className="flex items-center gap-1.5 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={(form.allowed_rate_pick_modes || []).includes(m)}
                  onChange={() => toggleArr('allowed_rate_pick_modes', m)}
                />
                {m.replace('_', ' ')}
              </label>
            ))}
          </div>
        </div>
      </div>
      )}

      {showDispatch && (
      <div>
        <h3 className="font-bold text-gray-900 mb-3">Dispatch controls</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className={label}>Default wait (seconds)</label>
            <input className={input} type="number" min="15" value={form.default_wait_seconds} onChange={(e) => set('default_wait_seconds', e.target.value)} />
          </div>
          <div>
            <label className={label}>Wait min (seconds)</label>
            <input className={input} type="number" min="15" value={form.wait_seconds_min} onChange={(e) => set('wait_seconds_min', e.target.value)} />
          </div>
          <div>
            <label className={label}>Wait max (seconds)</label>
            <input className={input} type="number" min="60" value={form.wait_seconds_max} onChange={(e) => set('wait_seconds_max', e.target.value)} />
          </div>
        </div>
        <div className="mt-3">
          <label className={label}>Allowed service types (none checked = all)</label>
          <div className="flex flex-wrap gap-3">
            {SERVICE_TYPES.map((s) => (
              <label key={s} className="flex items-center gap-1.5 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={(form.allowed_service_types || []).includes(s)}
                  onChange={() => toggleArr('allowed_service_types', s)}
                />
                {s}
              </label>
            ))}
          </div>
        </div>
      </div>
      )}

      {showApi && (
      <div>
        <h3 className="font-bold text-gray-900 mb-3">API</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="flex items-end pb-1">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={!!form.api_enabled} onChange={(e) => set('api_enabled', e.target.checked)} />
              API enabled
            </label>
          </div>
          <div>
            <label className={label}>Rate limit (req/min)</label>
            <input className={input} type="number" min="1" value={form.rate_limit_per_min} onChange={(e) => set('rate_limit_per_min', e.target.value)} />
          </div>
          <div>
            <label className={label}>Webhook URL</label>
            <input className={input} value={form.webhook_url || ''} onChange={(e) => set('webhook_url', e.target.value)} placeholder="https://..." />
          </div>
        </div>
      </div>
      )}

      {error && <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</p>}

      <div className="flex justify-end gap-2">
        {onCancel && (
          <button type="button" onClick={onCancel} className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-semibold text-gray-600">
            Cancel
          </button>
        )}
        <button type="submit" disabled={saving} className="px-6 py-2 bg-primary-600 text-white rounded-lg text-sm font-bold disabled:opacity-60">
          {saving ? 'Saving…' : sections === 'rates' ? 'Save Rates' : 'Save Enterprise'}
        </button>
      </div>
    </form>
  );
}

const Enterprises = () => {
  const navigate = useNavigate();
  const [showCreate, setShowCreate] = useState(false);

  const { data: items = [], isLoading, refetch } = useQuery({
    queryKey: ['adminEnterprises'],
    queryFn: () => EnterpriseAdminService.list(),
  });

  const { data: newInquiryCount = 0 } = useQuery({
    queryKey: ['enterpriseWebsiteInquiries', 'new-count'],
    queryFn: async () => {
      const { count, error } = await marketingAdmin
        .from('website_leads')
        .select('id', { count: 'exact', head: true })
        .eq('source', 'enterprise_page')
        .or('status.eq.new,status.is.null');
      if (error) return 0;
      return Number(count) || 0;
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary-500 border-t-transparent rounded-full spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Loading enterprises...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Enterprises (B2B)</h1>
          <p className="text-gray-600 mt-1">
            B2B accounts that register complaints via the /enterprise portal or REST API
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => navigate('/enterprises/inquiries')}
            className="relative flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-bold text-gray-800 hover:bg-gray-50"
          >
            <Inbox size={16} /> Inquiries
            {newInquiryCount > 0 && (
              <span className="rounded-full bg-amber-500 px-1.5 py-0.5 text-[11px] font-bold leading-none text-white">
                {newInquiryCount}
              </span>
            )}
          </button>
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg font-bold text-sm"
          >
            <Plus size={16} /> New Enterprise
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <p className="text-sm text-gray-600">Total accounts</p>
          <p className="text-2xl font-bold text-gray-900">{items.length}</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <p className="text-sm text-gray-600">Total wallet balance</p>
          <p className="text-2xl font-bold text-gray-900">
            ₹{items.reduce((s, e) => s + Number(e.wallet_balance || 0), 0).toLocaleString('en-IN')}
          </p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
          <p className="text-sm text-gray-600">Pending top-ups</p>
          <p className="text-2xl font-bold text-amber-600">
            {items.reduce((s, e) => s + Number(e.pending_topups || 0), 0)}
          </p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {items.length === 0 ? (
          <div className="py-16 text-center text-gray-500">
            <Building2 className="mx-auto mb-3 text-gray-300" size={40} />
            No enterprise accounts yet. Create the first one.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-gray-50 border-b border-gray-200 text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-4 py-3">Company</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Portal / mo</th>
                  <th className="px-4 py-3">Fee/complaint</th>
                  <th className="px-4 py-3">Mechanic pay</th>
                  <th className="px-4 py-3">Wallet</th>
                  <th className="px-4 py-3">Complaints</th>
                  <th className="px-4 py-3">Users</th>
                  <th className="px-4 py-3">Top-ups</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((e) => (
                  <tr
                    key={e.id}
                    onClick={() => navigate(`/enterprises/${e.id}`)}
                    className="hover:bg-gray-50 cursor-pointer"
                  >
                    <td className="px-4 py-3">
                      <p className="font-bold text-gray-900">{e.name}</p>
                      <p className="text-xs text-gray-500">{e.contact_email || e.contact_phone || ''}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-0.5 rounded text-xs font-bold ${
                          e.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                        }`}
                      >
                        {e.status === 'active' ? 'active' : 'deactivated'}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-semibold">
                      {Number(e.subscription_monthly_inr || 0) > 0
                        ? `₹${Number(e.subscription_monthly_inr).toLocaleString('en-IN')}`
                        : 'Free'}
                    </td>
                    <td className="px-4 py-3 font-semibold">₹{Number(e.per_complaint_fee || 0)}</td>
                    <td className="px-4 py-3 text-xs text-gray-600">
                      {String(e.mechanic_payment_mode || '').replace(/_/g, ' ')}
                    </td>
                    <td className="px-4 py-3 font-bold text-gray-900">
                      ₹{Number(e.wallet_balance || 0).toLocaleString('en-IN')}
                    </td>
                    <td className="px-4 py-3">{e.complaints_count || 0}</td>
                    <td className="px-4 py-3">{e.users_count || 0}</td>
                    <td className="px-4 py-3">
                      {Number(e.pending_topups || 0) > 0 ? (
                        <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-700 text-xs font-bold">
                          {e.pending_topups} pending
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={async (ev) => {
                          ev.stopPropagation();
                          const next = e.status === 'active' ? 'suspended' : 'active';
                          const ok = window.confirm(
                            next === 'suspended'
                              ? `Deactivate ${e.name}? They will not be able to use the portal.`
                              : `Activate ${e.name} again?`,
                          );
                          if (!ok) return;
                          await EnterpriseAdminService.setStatus(e.id, next);
                          await refetch();
                        }}
                        className={`text-xs font-bold hover:underline ${
                          e.status === 'active' ? 'text-red-600' : 'text-green-600'
                        }`}
                      >
                        {e.status === 'active' ? 'Deactivate' : 'Activate'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl w-full max-w-3xl p-6 my-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">New Enterprise Account</h2>
              <button onClick={() => setShowCreate(false)} className="p-1 text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>
            <EnterpriseForm
              onCancel={() => setShowCreate(false)}
              onSaved={async (id) => {
                setShowCreate(false);
                await refetch();
                if (id) navigate(`/enterprises/${id}`);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Enterprises;
